import { api } from '../core/client';

/** The editable outlet settings (restaurant profile + tax) shown on the panel Settings page. */
export interface ApiOutletSettings {
  name: string;
  cuisine: string;
  phone: string;
  address: string;
  gstin: string;
  cgst: string;
  sgst: string;
  serviceCharge: string;
  taxInclusive: boolean;
  /** Admin-set login/welcome banner image URL (empty = built-in default). */
  bannerImage: string;
  /** Dynamic theme (empty = app default). Mode = "light" | "dark"; colours are hex strings. */
  themeMode: string;
  themePrimary: string;
  themeSecondary: string;
  /** Day-wise kitchen open/close schedule that gates ordering (7 entries, day 0=Sun..6=Sat). */
  kitchenHours: ApiKitchenHour[];
  /** How employees punch attendance: pin | qr | photo | face (empty = pin). */
  attendanceMethod?: string;
  /** Admin-uploaded brand logo URL (shown on app, QR pages & invoices). */
  logo?: string;
  /**
   * Which billing screen the POS opens: `table` (default) or `counter`.
   *
   * These are two different billing models, not two skins: `table` SETTLES bills that already
   * exist (one per table, created when a customer orders); `counter` BUILDS a bill from nothing
   * by searching the item master. Empty reads as `table`.
   */
  posBillingMode?: string;
}

/** One day's kitchen window. day = 0 (Sun) .. 6 (Sat); open/close are 24h "HH:mm". */
export interface ApiKitchenHour {
  day: number;
  open: string;
  close: string;
  closed: boolean;
}

/** Whether the kitchen is accepting orders right now (for the storefront to gate ordering). */
export interface ApiKitchenStatus {
  open: boolean;
  /** Today's hours label, e.g. "10 AM – 10 PM" or "Closed today". */
  hoursToday: string;
  /** Customer-facing message shown when closed (empty when open). */
  message: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string | null;
  result: T;
}

/** Settings API — the outlet profile + tax config (GET/PUT /api/settings/outlet). */
export const settingsService = {
  getOutlet(): Promise<ApiOutletSettings> {
    return api.get<ApiEnvelope<ApiOutletSettings>>('/settings/outlet').then((r) => r.data.result);
  },

  saveOutlet(settings: ApiOutletSettings): Promise<ApiOutletSettings> {
    return api.put<ApiEnvelope<ApiOutletSettings>>('/settings/outlet', settings).then((r) => r.data.result);
  },

  /**
   * The print/document settings JSON (letterhead extras, chosen template, terms, toggles).
   * Returns '' when the outlet has never configured them — the caller falls back to defaults.
   * Resolves to '' on a network error too: a failed settings fetch must not stop someone printing.
   */
  getPrintSettings(): Promise<string> {
    return api
      .get<ApiEnvelope<{ json: string }>>('/settings/print')
      .then((r) => r.data.result?.json ?? '')
      .catch(() => '');
  },

  /** Persist the print/document settings JSON outlet-wide (so every terminal prints the same). */
  savePrintSettings(json: string): Promise<string> {
    return api.put<ApiEnvelope<{ json: string }>>('/settings/print', { json }).then((r) => r.data.result?.json ?? '');
  },

  /** Is the kitchen open right now (day-wise schedule)? Used by the storefront to gate ordering. */
  kitchenStatus(): Promise<ApiKitchenStatus> {
    return api
      .get<ApiEnvelope<ApiKitchenStatus>>('/settings/kitchen-status')
      .then((r) => r.data.result)
      .catch(() => ({ open: true, hoursToday: '', message: '' })); // fail-open: never block ordering on a fetch error
  }
};
